import Hls from 'hls.js'
import { useEffect, useRef, useState } from 'react'
import { assetCache } from '@/engine/asset-cache'
import type { PlaybackResult } from '@/types/player.types'
import { playerLogger } from '@/utils/player-logger'

interface Props {
  assetId: string
  assetUrl: string
  /** HLS master manifest URL. When present and asset is not yet cached locally,
   *  the player streams via ABR instead of waiting for the full MP4 download. */
  hlsUrl?: string
  /** SHA-256 esperado do arquivo original — validado no download antes de cachear/reproduzir. */
  checksum?: string
  durationSeconds: number
  /** Reporta o que de fato tocou — `played: false` se o vídeo nunca chegou a reproduzir (F-07). */
  onEnd: (result: PlaybackResult) => void
}

type VideoSource = { type: 'blob'; url: string } | { type: 'hls'; url: string } | { type: 'direct'; url: string }

const STARTUP_TIMEOUT_MS = 20_000
const STALL_TIMEOUT_MS = 15_000
const STALL_CHECK_INTERVAL_MS = 5_000

export function VideoRenderer({ assetId, assetUrl, hlsUrl, checksum, durationSeconds, onEnd }: Props) {
  const [source, setSource] = useState<VideoSource | null>(null)
  const [visible, setVisible] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const cutoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stallTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** Evita duplo fim (timer de duração + evento `ended` / reentrância). */
  const endSentRef = useRef(false)
  const playbackStartedRef = useRef(false)
  const lastProgressAtRef = useRef(Date.now())
  /** Epoch ms do primeiro evento `playing` — null enquanto nada foi exibido. */
  const playingSinceRef = useRef<number | null>(null)
  /** currentTime no primeiro `playing` (normalmente 0). */
  const playingFromTimeRef = useRef(0)
  /** Já caiu do HLS para o MP4 direto neste item — o fallback acontece no máximo uma vez. */
  const hlsFallbackUsedRef = useRef(false)
  const onEndRef = useRef(onEnd)
  onEndRef.current = onEnd

  // ─── Source resolution ────────────────────────────────────────────────────
  //
  // Decision tree (evaluated once per item, when assetId/assetUrl/hlsUrl changes):
  //
  //  1. Blob in memory (instant)        → type: 'blob'
  //  2. Blob in IndexedDB (fast ~100ms) → type: 'blob'
  //  3. hlsUrl + Hls.isSupported()      → type: 'hls'
  //  4. Fallback                        → type: 'direct'  (browser streaming)
  //
  // Avoid full MP4 download/hash while video is playing; that can freeze kiosk browsers silently.
  //
  // LOW-8 — integridade no first-play: o stream (casos 3/4) NÃO passa pela validação de checksum.
  // Para não deixar tela preta, o stream continua permitido, mas:
  //  - o play é reportado como `contentVerified: false` (nunca como verificado);
  //  - ao FIM do item, o MP4 é baixado e validado em background (fila serial) → próximos plays
  //    saem do blob verificado;
  //  - se essa validação provar que o conteúdo diverge do checksum da agenda, o asset deixa de
  //    tocar (nem via stream) até a agenda trazer um checksum novo.

  useEffect(() => {
    let cancelled = false
    endSentRef.current = false
    hlsFallbackUsedRef.current = false
    setSource(null)
    setVisible(false)

    // Destroy any lingering HLS instance from a previous item
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    const resolveSource = async () => {
      // Conteúdo já comprovadamente divergente do checksum aprovado → não reproduzir (LOW-8)
      if (assetCache.isRejected(assetId, checksum)) {
        throw new Error('Asset failed checksum verification; refusing to stream')
      }

      // Case 1: already in memory — no async work needed
      const memUrl = assetCache.peekBlobUrl(assetId, checksum)
      if (memUrl) {
        if (!cancelled) setSource({ type: 'blob', url: memUrl })
        return
      }

      // Case 2: in IndexedDB — fast async read, still far cheaper than a download
      const cached = await assetCache.isReady(assetId, checksum)
      if (cached) {
        const blobUrl = await assetCache.ensureReady(assetId, assetUrl, checksum)
        if (!cancelled) setSource({ type: 'blob', url: blobUrl })
        return
      }

      // Case 3: asset not cached locally — stream HLS without background MP4 caching
      if (hlsUrl && Hls.isSupported()) {
        if (!cancelled) setSource({ type: 'hls', url: hlsUrl })
        return
      }

      // Case 4: no HLS available — let the browser stream directly
      if (!cancelled) setSource({ type: 'direct', url: assetUrl })
    }

    resolveSource().catch((err) => {
      playerLogger.warn('[VideoRenderer] Source resolution failed, advancing', err)
      if (cancelled || endSentRef.current) return
      endSentRef.current = true
      onEndRef.current({ played: false })
    })

    return () => {
      cancelled = true
      if (cutoffTimerRef.current) clearTimeout(cutoffTimerRef.current)
      if (startupTimerRef.current) clearTimeout(startupTimerRef.current)
      if (stallTimerRef.current) clearInterval(stallTimerRef.current)
    }
  }, [assetId, assetUrl, hlsUrl, checksum])

  // ─── Playback ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!source || !videoRef.current) return

    endSentRef.current = false
    playbackStartedRef.current = false
    playingSinceRef.current = null
    playingFromTimeRef.current = 0

    const video = videoRef.current

    /** Mede o que foi exibido: avanço de currentTime (exclui stalls), wall clock como fallback. */
    const buildResult = (): PlaybackResult => {
      const startedAt = playingSinceRef.current
      if (startedAt === null) return { played: false }
      const byMediaTime = video.currentTime - playingFromTimeRef.current
      const actualSeconds =
        Number.isFinite(byMediaTime) && byMediaTime >= 0 ? byMediaTime : (Date.now() - startedAt) / 1000
      if (actualSeconds <= 0) return { played: false }
      // Só o blob validado contra o checksum da agenda conta como verificado (LOW-8).
      const contentVerified = source.type === 'blob' && !!checksum
      return { played: true, actualSeconds, startedAt, contentVerified }
    }

    const signalEnd = () => {
      if (endSentRef.current) return
      endSentRef.current = true
      if (cutoffTimerRef.current) {
        clearTimeout(cutoffTimerRef.current)
        cutoffTimerRef.current = null
      }
      if (startupTimerRef.current) {
        clearTimeout(startupTimerRef.current)
        startupTimerRef.current = null
      }
      if (stallTimerRef.current) {
        clearInterval(stallTimerRef.current)
        stallTimerRef.current = null
      }
      // Só depois de um stream que de fato tocou: após falha, o download competiria por banda e, com a
      // mídia inacessível, falharia do mesmo jeito.
      if (source.type !== 'blob' && playingSinceRef.current !== null) {
        // Fim do stream não verificado: baixa + valida em background para os próximos plays (LOW-8).
        void assetCache.verifyInBackground(assetId, assetUrl, checksum)
      }
      onEndRef.current(buildResult())
    }

    const startPlayback = () => {
      if (playbackStartedRef.current) return
      playbackStartedRef.current = true
      if (startupTimerRef.current) {
        clearTimeout(startupTimerRef.current)
        startupTimerRef.current = null
      }
      setVisible(true)
      lastProgressAtRef.current = Date.now()
      void video.play().catch((err) => {
        playerLogger.warn('[VideoRenderer] video.play failed, advancing', err)
        signalEnd()
      })

      if (stallTimerRef.current) clearInterval(stallTimerRef.current)
      stallTimerRef.current = setInterval(() => {
        if (endSentRef.current || video.paused || video.ended) return
        const stalledForMs = Date.now() - lastProgressAtRef.current
        if (stalledForMs < STALL_TIMEOUT_MS) return
        playerLogger.warn('[VideoRenderer] Playback stalled without timeupdate, advancing', {
          sourceType: source.type,
          assetId,
          currentTime: video.currentTime,
          stalledForMs
        })
        signalEnd()
      }, STALL_CHECK_INTERVAL_MS)

      cutoffTimerRef.current = setTimeout(() => {
        signalEnd()
      }, durationSeconds * 1000)
    }

    const handleEnded = () => {
      signalEnd()
    }

    const handleError = () => {
      playerLogger.warn('[VideoRenderer] Playback error, advancing')
      signalEnd()
    }

    const handleTimeUpdate = () => {
      lastProgressAtRef.current = Date.now()
    }

    const handlePlaying = () => {
      if (playingSinceRef.current !== null) return
      playingSinceRef.current = Date.now()
      playingFromTimeRef.current = Number.isFinite(video.currentTime) ? video.currentTime : 0
    }

    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('playing', handlePlaying)
    video.addEventListener('canplay', startPlayback, { once: true })

    startupTimerRef.current = setTimeout(() => {
      if (playbackStartedRef.current || endSentRef.current) return
      playerLogger.warn('[VideoRenderer] Playback startup timeout, advancing', {
        sourceType: source.type,
        assetId
      })
      signalEnd()
    }, STARTUP_TIMEOUT_MS)

    if (source.type === 'hls') {
      // ── HLS path via hls.js ────────────────────────────────────��─────────
      const hls = new Hls({
        // Start at the lowest quality for fastest first frame,
        // then let ABR ramp up as bandwidth allows.
        startLevel: 0,
        // Cap network retries so a dead CDN doesn't stall the playlist forever.
        manifestLoadingMaxRetry: 2,
        levelLoadingMaxRetry: 2,
        fragLoadingMaxRetry: 2
      })
      hlsRef.current = hls
      hls.loadSource(source.url)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        startPlayback()
      })
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return
        const code = data.response?.code
        // Payload enxuto: o ErrorData inteiro é enorme e tomava o ring buffer de logs.
        const summary = { type: data.type, details: data.details, code, assetId }
        // Status 0 (ou sem resposta) = CORS/bloqueio de rede: o hls.js não tenta de novo. O <video src>
        // direto não depende de CORS — tenta uma vez antes de desistir do item. Só antes de algo tocar:
        // no meio do stream o fallback reiniciaria o vídeo do zero e perderia o tempo já exibido.
        if (
          data.type === Hls.ErrorTypes.NETWORK_ERROR &&
          (code ?? 0) === 0 &&
          assetUrl &&
          playingSinceRef.current === null &&
          !hlsFallbackUsedRef.current
        ) {
          hlsFallbackUsedRef.current = true
          playerLogger.warn('[VideoRenderer] HLS blocked (status 0), falling back to direct MP4', summary)
          setSource({ type: 'direct', url: assetUrl })
          return
        }
        playerLogger.warn('[VideoRenderer] Fatal HLS error, advancing', summary)
        handleError()
      })
    } else {
      // ── Blob path ────────────────────────────────────────────────────────
      video.src = source.url
    }

    return () => {
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('playing', handlePlaying)
      video.removeEventListener('canplay', startPlayback)
      if (cutoffTimerRef.current) {
        clearTimeout(cutoffTimerRef.current)
        cutoffTimerRef.current = null
      }
      if (startupTimerRef.current) {
        clearTimeout(startupTimerRef.current)
        startupTimerRef.current = null
      }
      if (stallTimerRef.current) {
        clearInterval(stallTimerRef.current)
        stallTimerRef.current = null
      }

      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      video.removeAttribute('src')
      video.load()
    }
  }, [source, durationSeconds, assetId, assetUrl, checksum])

  if (!source) return null

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      preload="auto"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease',
        background: '#000'
      }}
    />
  )
}
